import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Phone, MessageCircle, XCircle, Loader2, Wallet, Clock, Info, Package, User, Send, Image as ImageIcon, ShieldCheck, Camera, Heart, Gift } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { StatusBadge, Stepper } from "./ui";
import ReportDialog from "./ReportDialog";
import { TipDialog, TrustBadge } from "./TipAndTrust";
import { useAuth } from "../../context/AuthContext";
import { ridesApi, inr, errorMessage, titleCase, publicApi } from "../../lib/api";
import { PayDialog } from "./PayDialog";
import RefundTracker from "./RefundTracker";
export { PayDialog };

function GoodsPanel({ ride }) {
  const [sending, setSending] = useState(false);
  const [trust, setTrust] = useState(null);
  useEffect(() => {
    if (ride.driver_user_id) publicApi.partnerTrust(ride.driver_user_id).then(setTrust).catch(() => {});
  }, [ride.driver_user_id]);
  const isActive = ["searching", "fee_offer", "driver_assigned", "driver_arriving", "in_progress"].includes(ride.status);
  const isDone = ride.status === "completed";
  const proofs = [
    ride.pickup_photo_id && { id: ride.pickup_photo_id, label: "Pickup" },
    ride.proof_photo_id && { id: ride.proof_photo_id, label: "Delivered" },
    ride.proof_signature_id && { id: ride.proof_signature_id, label: "Signature" },
  ].filter(Boolean);
  const shareReceiver = async () => {
    setSending(true);
    try {
      const res = await ridesApi.notifyReceiver(ride.ride_id);
      toast.success(res.whatsapp ? "Tracking link sent to the receiver on WhatsApp" : "Tracking link ready (WhatsApp not configured)");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSending(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-surface-200 bg-surface-50 p-4" data-testid="goods-panel">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Package className="h-3.5 w-3.5" /> Goods delivery</p>
      {trust?.eligible ? <TrustBadge trust={trust} /> : null}
      {(ride.receiver_name || ride.receiver_phone) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {ride.receiver_name && <span className="flex items-center gap-1.5 text-ink-soft"><User className="h-3.5 w-3.5" /> {ride.receiver_name}</span>}
          {ride.receiver_phone && <a href={`tel:${ride.receiver_phone}`} className="flex items-center gap-1.5 text-ink-soft hover:text-brand"><Phone className="h-3.5 w-3.5" /> {ride.receiver_phone}</a>}
          {ride.weight_kg != null && <span className="text-ink-muted">{ride.weight_kg} kg</span>}
        </div>
      )}
      {ride.cod_payer && <p className="text-xs text-ink-muted">{ride.cod_payer === "receiver" ? "Receiver pays the delivery fee on drop-off" : "You pay the delivery fee"}</p>}
      {isDone && ride.drop_code_verified ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid="goods-verified-badge"><ShieldCheck className="h-3.5 w-3.5" /> Code-verified handoff</span>
      ) : isDone && ride.completed_without_code ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-200 px-2.5 py-1 text-xs font-semibold text-ink-soft" data-testid="goods-photoproof-badge"><Camera className="h-3.5 w-3.5" /> Completed with photo proof</span>
      ) : null}
      {isActive && ride.receiver_phone && (
        <Button size="sm" variant="outline" onClick={shareReceiver} disabled={sending} className="rounded-full" data-testid="goods-share-receiver">
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Share tracking with receiver
        </Button>
      )}
      {isDone && proofs.length > 0 && (
        <div data-testid="goods-proof">
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink-muted"><ImageIcon className="h-3.5 w-3.5" /> Delivery proof</p>
          <div className="grid grid-cols-3 gap-2">
            {proofs.map((p) => (
              <a key={p.id} href={ridesApi.photoUrl(ride.ride_id, p.id)} target="_blank" rel="noreferrer" data-testid={`goods-proof-${p.label.toLowerCase()}`}>
                <img src={ridesApi.photoUrl(ride.ride_id, p.id)} alt={p.label} className="h-24 w-full rounded-lg border border-surface-200 object-cover" />
                <p className="mt-0.5 text-center text-[11px] text-ink-muted">{p.label}</p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const RIDE_STEPS = [  { key: "searching", label: "Finding driver" },
  { key: "driver_assigned", label: "Driver assigned" },
  { key: "driver_arriving", label: "Arriving" },
  { key: "in_progress", label: "On trip" },
  { key: "completed", label: "Completed" },
];

export function RatingStars({ value, onChange, size = "h-7 w-7" }) {
  return (
    <div className="flex gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} onClick={() => onChange?.(n)} className="transition-transform hover:scale-110" aria-label={`${n} stars`} data-testid={`star-${n}`}>
          <Star className={`${size} ${n <= value ? "fill-brand text-brand" : "text-surface-300"}`} />
        </button>
      ))}
    </div>
  );
}

export function RateDialog({ open, onOpenChange, title = "Rate your trip", onRate, subRatings = false }) {
  const [rating, setRating] = useState(5);
  const [foodRating, setFoodRating] = useState(5);
  const [deliveryRating, setDeliveryRating] = useState(5);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { rating, review: review.trim() || null };
      if (subRatings) { payload.food_rating = foodRating; payload.delivery_rating = deliveryRating; }
      await onRate(payload); onOpenChange(false);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="rate-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Your feedback keeps the network honest.</DialogDescription></DialogHeader>
        <div className="flex flex-col items-center gap-1 py-1">
          {subRatings && <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Overall</p>}
          <RatingStars value={rating} onChange={setRating} size="h-9 w-9" />
        </div>
        {subRatings && (
          <div className="grid grid-cols-2 gap-3 border-t border-surface-200 pt-3">
            <div className="flex flex-col items-center gap-1"><p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Food</p><RatingStars value={foodRating} onChange={setFoodRating} size="h-6 w-6" /></div>
            <div className="flex flex-col items-center gap-1"><p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Delivery</p><RatingStars value={deliveryRating} onChange={setDeliveryRating} size="h-6 w-6" /></div>
          </div>
        )}
        <Textarea value={review} onChange={(e) => setReview(e.target.value)} placeholder="Anything to add? (optional)" rows={3} data-testid="rate-review" />
        <Button onClick={submit} disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="rate-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit rating</Button>
      </DialogContent>
    </Dialog>
  );
}

export function DriverCard({ driver, compact = false }) {
  if (!driver) return null;
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "rounded-2xl bg-surface-100 p-4"}`} data-testid="driver-card">
      {driver.photo ? <img src={driver.photo} alt={driver.name} className="h-12 w-12 rounded-full object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-full bg-graphite text-white">{driver.name?.[0]}</span>}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{driver.name}</p>
        <p className="truncate text-xs text-ink-soft">{[driver.vehicle_model, driver.plate].filter(Boolean).join(" \u00b7 ")}</p>
      </div>
      <div className="text-right text-xs">
        <p className="inline-flex items-center gap-1 font-semibold text-ink"><Star className="h-3.5 w-3.5 fill-brand text-brand" />{driver.rating}</p>
        {driver.trips != null && <p className="text-ink-muted">{driver.trips} trips</p>}
      </div>
    </div>
  );
}

export function ActiveRidePanel({ ride, onChange, onDone }) {
  const { user } = useAuth();
  const [payOpen, setPayOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const terminal = ["completed", "cancelled"].includes(ride.status);
  const searching = ride.status === "searching";

  // Poll the ride while active
  useEffect(() => {
    if (terminal) return undefined;
    const t = setInterval(() => ridesApi.get(ride.ride_id).then(onChange).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [ride.ride_id, terminal, onChange]);

  // No-driver timeout while searching
  useEffect(() => {
    if (!searching) { setExpired(false); return undefined; }
    const exp = ride.search_expires_at ? new Date(ride.search_expires_at).getTime() : new Date(ride.created_at).getTime() + 60000;
    const check = () => setExpired(Date.now() > exp);
    check();
    const t = setInterval(check, 1000);
    return () => clearInterval(t);
  }, [searching, ride.search_expires_at, ride.created_at]);

  // Buzz the rider once when the driver is ~1 minute from pickup, and again near drop.
  const buzzedPhase = React.useRef(null);
  useEffect(() => {
    const phase = ride.status === "in_progress" ? "drop" : (["driver_assigned", "driver_arriving"].includes(ride.status) ? "pickup" : null);
    if (!phase) { return; }
    if (ride.eta_min != null && ride.eta_min <= 1 && buzzedPhase.current !== phase) {
      buzzedPhase.current = phase;
      try { if (navigator.vibrate) navigator.vibrate([400, 150, 400]); } catch { /* ignore */ }
      toast.success(phase === "pickup" ? "Your driver is almost here!" : "Almost at your destination", { description: phase === "pickup" ? "About a minute away for pickup." : "You'll reach your drop in about a minute." });
    }
  }, [ride.eta_min, ride.status]);

  // Notify the rider once whenever a new extra charge (far-pickup or waiting) appears.
  const notifiedSurge = React.useRef("");
  useEffect(() => {
    const key = `${ride.distance_fee || 0}-${ride.waiting_charge || 0}`;
    if (key === "0-0") { notifiedSurge.current = ""; return; }
    if (notifiedSurge.current !== key) {
      notifiedSurge.current = key;
      const parts = [];
      if (ride.distance_fee > 0) parts.push(`₹${ride.distance_fee} far-pickup fee`);
      if (ride.waiting_charge > 0) parts.push(`₹${ride.waiting_charge} waiting charge`);
      toast.info("Extra charge added to your fare", { description: parts.join(" + ") });
    }
  }, [ride.distance_fee, ride.waiting_charge]);

  const cancel = async () => {
    setBusy(true);
    try { const r = await ridesApi.cancel(ride.ride_id); onChange(r); toast.success(r.cancel_fee ? `Ride cancelled — ₹${r.cancel_fee} cancellation fee applied` : "Ride cancelled"); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const submitCancel = async (reason, note) => {
    setBusy(true);
    try {
      const r = await ridesApi.cancel(ride.ride_id, reason ? { reason, note: note || "" } : {});
      onChange(r);
      setCancelOpen(false);
      toast.success(r.cancel_fee ? `Ride cancelled — ₹${r.cancel_fee} cancellation fee applied` : "Ride cancelled");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const refundDeposit = async () => {
    setBusy(true);
    try { const r = await ridesApi.refundDeposit(ride.ride_id); onChange(r); toast.success("Deposit refund started — it'll reach your account in 5–7 days"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const rate = async (body) => {
    try { onChange(await ridesApi.rate(ride.ride_id, body)); toast.success("Thanks for rating!"); } catch (e) { toast.error(errorMessage(e)); throw e; }
  };
  const feeRespond = async (approve) => {
    setBusy(true);
    try { onChange(await ridesApi.feeResponse(ride.ride_id, approve)); toast.success(approve ? "Pickup fee added — driver confirmed" : "Looking for a closer driver"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="active-ride-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Ride {ride.ride_id.slice(-6).toUpperCase()}</p>
          <p className="mt-1 text-2xl font-bold text-ink">{inr(ride.fare)}</p>
          <p className="text-xs text-ink-soft">{ride.distance_km} km · {ride.duration_min} min · {titleCase(ride.vehicle_type)}</p>
        </div>
        <StatusBadge status={ride.status} />
      </div>
      {ride.fee_waived && (
        <div className="flex items-center gap-2 rounded-2xl border border-green-300 bg-green-50 p-3 text-sm font-semibold text-green-700" data-testid="ride-fee-waived">
          <Gift className="h-4 w-4" /> Far-pickup fee waived 🎉 — saved you ₹{ride.fee_waived_amount || 0} with your loyalty perk.
        </div>
      )}
      {(ride.distance_fee > 0 || ride.waiting_charge > 0) && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="ride-surcharges">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><Info className="h-4 w-4" /> Extra charges applied</p>
          <div className="mt-2 space-y-1 text-sm text-amber-900">
            {ride.distance_fee > 0 && (
              <div className="flex justify-between"><span>Far-pickup fee</span><span className="font-bold">₹{ride.distance_fee}</span></div>
            )}
            {ride.waiting_charge > 0 && (
              <div className="flex justify-between"><span>Waiting charge ({ride.waiting_min} min)</span><span className="font-bold">₹{ride.waiting_charge}</span></div>
            )}
          </div>
          <p className="mt-2 text-xs text-amber-700">These are added to your fare and shown here so there are no surprises.</p>
        </div>
      )}
      {ride.applied_cancel_balance > 0 && (
        <div className="rounded-xl bg-surface-100 px-3 py-2 text-xs text-ink-soft" data-testid="ride-applied-cancel-balance">Includes ₹{ride.applied_cancel_balance} outstanding cancellation fee from a previous ride.</div>
      )}
      {ride.status === "cancelled" && (ride.cancel_info?.text || ride.cancelled_reason) && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="ride-cancel-reason">{ride.cancel_info?.text || ride.cancelled_reason}</div>
      )}
      {ride.prior_driver_cancelled && ["searching", "fee_offer"].includes(ride.status) && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="driver-cancelled-choice">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><Info className="h-4 w-4" /> Your driver cancelled</p>
          {ride.driver_release_reason && <p className="mt-1 text-xs font-medium text-amber-900" data-testid="driver-cancel-reason-shown">Reason: {ride.driver_release_reason}</p>}
          <p className="mt-1 text-xs text-amber-800">We're finding you another driver now. You can keep waiting, or get your booking deposit back and end the booking.</p>
          <WaitingTimer since={ride.search_cycle_started_at} />
          <Button onClick={refundDeposit} disabled={busy} variant="outline" className="mt-3 rounded-full border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="ride-refund-deposit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />} Refund my deposit ({inr(ride.amount_paid || ride.deposit_amount)})</Button>
        </div>
      )}
      {ride.is_intercity && (ride.deposit_amount > 0 || ride.amount_paid > 0) && (
        <div className="rounded-2xl border border-surface-300 bg-white p-4" data-testid="deposit-receipt">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Payment summary</p>
          <div className="mt-2 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-ink-soft">{ride.status === "completed" ? "Final fare" : "Estimated fare"}</span><span className="font-semibold text-ink">{inr(ride.fare)}</span></div>
            <div className="flex justify-between"><span className="text-ink-soft">{ride.prepaid_full ? "Paid online in advance" : "Booking deposit paid online"}</span><span className="font-semibold text-ink">− {inr(ride.amount_paid || ride.deposit_amount)}</span></div>
            {ride.status === "cancelled" && ride.cancel_fee > 0 && (
              <div className="flex justify-between text-red-700"><span>Deposit kept (non-refundable)</span><span className="font-semibold">{inr(ride.cancel_fee)}</span></div>
            )}
            {ride.status === "cancelled" && ride.refund_amount > 0 && (
              <div className="flex justify-between text-green-700"><span>Refunded to you</span><span className="font-semibold">{inr(ride.refund_amount)}</span></div>
            )}
            {ride.status !== "cancelled" && (
              <div className="flex justify-between border-t border-surface-200 pt-1.5"><span className="text-ink-soft">Balance payable to driver</span><span className="font-bold text-brand">{inr(Math.max(0, (ride.fare || 0) - (ride.amount_paid || ride.deposit_amount || 0)))}</span></div>
            )}
          </div>
          {ride.status !== "cancelled" && <p className="mt-2 text-[11px] text-ink-muted">Your deposit is adjusted into the final bill. Tolls and waiting (if any) are added by the driver.</p>}
        </div>
      )}
      {ride.refund_tracker && <RefundTracker tracker={ride.refund_tracker} />}
      {ride.status !== "cancelled" && <Stepper steps={RIDE_STEPS} current={ride.status} />}
      <div className="space-y-2 text-sm">
        <p className="flex gap-2"><span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-graphite" /><span className="text-ink">{ride.pickup_address}</span></p>
        <p className="flex gap-2"><span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-brand" /><span className="text-ink">{ride.drop_address}</span></p>
      </div>
      {ride.purpose === "goods" && <GoodsPanel ride={ride} />}
      {searching ? (
        expired ? (
          <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4 text-center" data-testid="no-drivers">
            <p className="text-sm font-semibold text-ink">No drivers available right now</p>
            <p className="mt-1 text-xs text-ink-soft">All nearby drivers are busy. Please try booking again in a moment.</p>
            <Button onClick={async () => { await cancel(); onDone && onDone(); }} disabled={busy} className="mt-3 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="no-drivers-retry">Try again</Button>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-2xl border border-graphite-200 bg-graphite-50 p-4" data-testid="searching-drivers">
            <Loader2 className="h-5 w-5 animate-spin text-graphite" />
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink">Finding a driver near you…</p><p className="text-xs text-ink-soft">We're ringing nearby drivers — the first to accept gets your trip.</p></div>
            <Button variant="ghost" onClick={() => setCancelOpen(true)} disabled={busy} className="rounded-full text-red-700 hover:bg-red-50" data-testid="ride-cancel">Cancel</Button>
          </div>
        )
      ) : (
        ride.driver && (
          <div className="space-y-2">
            <DriverCard driver={ride.driver} />
            {ride.driver.phone && <a href={`tel:${ride.driver.phone}`} data-testid="ride-call-driver" className="flex w-full items-center justify-center gap-2 rounded-full border border-surface-200 py-2.5 text-sm font-semibold text-ink hover:bg-surface-50"><Phone className="h-4 w-4" /> Call driver</a>}
          </div>
        )
      )}
      {ride.status === "fee_offer" && ride.fee_offer && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="fee-offer">
          <p className="text-sm font-semibold text-ink">Nearest driver is {ride.fee_offer.distance_km} km away</p>
          <p className="mt-1 text-xs text-ink-soft">Add a ₹{ride.fee_offer.amount} pickup distance fee to confirm them now, or wait for a closer driver to accept.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => feeRespond(true)} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="fee-approve">Add ₹{ride.fee_offer.amount} &amp; confirm</Button>
            <Button variant="outline" onClick={() => feeRespond(false)} disabled={busy} className="rounded-full" data-testid="fee-decline">Wait for closer</Button>
          </div>
        </div>
      )}
      {ride.eta_min != null && ["driver_assigned", "driver_arriving", "in_progress"].includes(ride.status) && (
        <p className="inline-flex items-center gap-2 rounded-xl bg-graphite-50 px-3 py-2 text-sm font-semibold text-graphite" data-testid="ride-eta"><Clock className="h-4 w-4" /> {ride.status === "in_progress" ? `Arriving at drop in ~${ride.eta_min} min` : `Driver arriving in ~${ride.eta_min} min`}</p>
      )}
      {ride.start_code && ["driver_assigned", "driver_arriving"].includes(ride.status) && (
        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4 text-center" data-testid="ride-start-code">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Your ride code</p>
          <p className="mt-1 text-4xl font-black tracking-[0.3em] text-brand" data-testid="ride-start-code-value">{ride.start_code}</p>
          <p className="mt-1 text-xs text-ink-soft">Share this with your driver. They must enter it to start the trip — your safety check.</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {!terminal && !searching && (
          <>
            <Button asChild variant="outline" className="rounded-full"><Link to={`/app/chat/${ride.ride_id}`} data-testid="ride-chat"><MessageCircle className="mr-2 h-4 w-4" /> Chat</Link></Button>
            {ride.driver?.phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${ride.driver.phone}`}><Phone className="mr-2 h-4 w-4" /> Call</a></Button>}
            {ride.driver && <ReportDialog contextType="ride" contextId={ride.ride_id} triggerLabel="Report" />}
            {ride.status !== "in_progress" && <Button variant="ghost" onClick={() => setCancelOpen(true)} disabled={busy} className="rounded-full text-red-700 hover:bg-red-50" data-testid="ride-cancel"><XCircle className="mr-2 h-4 w-4" /> Cancel</Button>}
          </>
        )}
        {ride.status === "completed" && ride.payment_status !== "paid" && <Button onClick={() => setPayOpen(true)} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="ride-pay"><Wallet className="mr-2 h-4 w-4" /> Pay {inr(ride.fare)}</Button>}
        {ride.status === "completed" && !ride.rating && <Button onClick={() => setRateOpen(true)} variant="outline" className="rounded-full" data-testid="ride-rate"><Star className="mr-2 h-4 w-4" /> Rate trip</Button>}
        {ride.status === "completed" && ride.rating && <span className="inline-flex items-center gap-1 text-sm text-ink-soft"><Star className="h-4 w-4 fill-brand text-brand" /> You rated {ride.rating}/5</span>}
        {ride.status === "completed" && ride.payment_status === "paid" && ride.driver_user_id && !ride.tip_extra && <Button onClick={() => setTipOpen(true)} variant="outline" className="rounded-full text-rose-600 hover:bg-rose-50" data-testid="ride-tip"><Heart className="mr-2 h-4 w-4" /> Say thanks</Button>}
        {ride.status === "completed" && ride.tip_extra ? <span className="inline-flex items-center gap-1 text-sm text-rose-600" data-testid="ride-tipped"><Heart className="h-4 w-4 fill-rose-500 text-rose-500" /> Tipped {inr(ride.tip_extra)}</span> : null}
        {terminal && onDone && <Button variant="ghost" onClick={onDone} className="ml-auto rounded-full" data-testid="ride-done">Book another</Button>}
      </div>
      <PayDialog open={payOpen} onOpenChange={setPayOpen} amount={ride.fare} kind="ride" refId={ride.ride_id} onPaid={onChange} description={`Ride ${ride.pickup_address || ""} → ${ride.drop_address || ""}`} />
      <RateDialog open={rateOpen} onOpenChange={setRateOpen} onRate={rate} />
      <TipDialog open={tipOpen} onOpenChange={setTipOpen} kind="ride" refId={ride.ride_id} partnerName={ride.driver?.name} user={user} onDone={onChange} />
      <CancelReasonDialog open={cancelOpen} onOpenChange={setCancelOpen} ride={ride} busy={busy} onConfirm={submitCancel} />
    </div>
  );
}


const RIDE_CANCEL_REASONS = ["Changed plans", "Driver too far / taking long", "Found another ride", "Booked by mistake", "Other"];

function WaitingTimer({ since }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = since ? new Date(since).getTime() : Date.now();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [since]);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const dragging = elapsed >= 120;
  return (
    <p className={`mt-2 flex items-center gap-1.5 text-xs font-semibold ${dragging ? "text-red-700" : "text-amber-800"}`} data-testid="waiting-timer">
      <Clock className="h-3.5 w-3.5" /> Re-searching for {mm}:{ss}{dragging ? " — taking a while? Grab your deposit back below." : ""}
    </p>
  );
}

function CancelReasonDialog({ open, onOpenChange, ride, busy, onConfirm }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setReason(null); setNote(""); } }, [open]);
  const accepted = ride.driver && !["searching", "fee_offer"].includes(ride.status);
  const isIntercityDep = ride.is_intercity && ride.deposit_paid;
  const feeMsg = isIntercityDep
    ? "Your 10% booking deposit is non-refundable when you cancel. Any amount paid above it is refunded."
    : accepted
      ? (ride.payment_status === "paid" ? "A ₹20 cancellation fee will be kept and the rest refunded to you." : "A ₹20 cancellation fee applies and will be added to your next ride.")
      : "No charge — a driver hasn't accepted yet.";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cancel-reason-dialog">
        <DialogHeader>
          <DialogTitle>Cancel this ride?</DialogTitle>
          <DialogDescription>{feeMsg} Tell us why — it helps your driver and us.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {RIDE_CANCEL_REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(reason === r ? null : r)} data-testid={`cancel-reason-${r.replace(/[^a-z]+/gi, "-").toLowerCase().replace(/-+$/,"")}`} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${reason === r ? "border-brand bg-brand text-white" : "border-surface-300 bg-white text-ink-soft hover:border-brand-200"}`}>{r}</button>
          ))}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={280} placeholder="Add a note (optional)" className="mt-1" data-testid="cancel-reason-note" />
        <div className="mt-2 flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full" data-testid="cancel-reason-keep">Keep ride</Button>
          <Button onClick={() => onConfirm(reason, note.trim())} disabled={busy} className="flex-1 rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="cancel-reason-confirm">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Cancel ride</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
